import classNames from 'classnames'
import { observer } from 'mobx-react'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import style from './FullscreenDrag.module.less'
import { commonUtils } from '@/helpers/commonUtils'
import { Progress } from 'antd'

type FullscreenDragProps = {
  dragAreaRef: HTMLElement | null
  uploadingPdf: boolean
  paringPdf2Html: boolean
  uploadPercent: number
  disabled: boolean
  handleCancelUpload: () => void
  handleDropFile: (file: File) => void
}

export interface FullscreenDragRef {
  setShowBlurArea: (show: boolean) => void
}

const FullscreenDrag = observer(
  forwardRef<FullscreenDragRef, FullscreenDragProps>((props, ref) => {
    const {
      dragAreaRef,
      uploadingPdf,
      paringPdf2Html,
      uploadPercent,
      disabled,
      handleCancelUpload,
      handleDropFile,
    } = props
    const popupAreaRef = useRef<HTMLDivElement | null>(null)

    const [showBlurArea, setShowBlurArea] = useState(false)

    useImperativeHandle(ref, () => ({
      setShowBlurArea,
    }))

    const handleCalcProgressLeft = (uploadPercent: number) => {
      return (271 - 12) * (uploadPercent / 100)
    }

    useEffect(() => {
      const dragOverEvent = (e: DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        const hasFiles = Array.from(e.dataTransfer?.types ?? []).includes('Files')
        if (hasFiles && !disabled) {
          setShowBlurArea(true)
        }
      }

      const dragLeaveEvent = (e: DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (!uploadingPdf) {
          setShowBlurArea(false)
        }
      }

      const popupDragoverEvent = (e: DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
      }

      const popupDragEvent = (e: DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (uploadingPdf) {
          return
        }
        if (e.dataTransfer?.files) {
          const dropFile = e.dataTransfer.files[0]
          if (dropFile && !disabled) {
            handleDropFile(dropFile)
            return
          }
        }
        setShowBlurArea(false)
      }

      // 监听拖拽开始事件
      dragAreaRef?.addEventListener('dragover', dragOverEvent)

      // 监听拖拽结束事件
      popupAreaRef.current?.addEventListener('dragleave', dragLeaveEvent)

      // 监听拖拽开始事件
      popupAreaRef.current?.addEventListener('dragover', popupDragoverEvent)

      // 监听拖拽事件
      popupAreaRef.current?.addEventListener('drop', popupDragEvent)

      return () => {
        // 监听拖拽开始事件
        dragAreaRef?.removeEventListener('dragover', dragOverEvent)

        // 监听拖拽结束事件
        popupAreaRef.current?.removeEventListener('dragleave', dragLeaveEvent)

        // 监听拖拽开始事件
        popupAreaRef.current?.removeEventListener('dragover', popupDragoverEvent)

        // 监听拖拽事件
        popupAreaRef.current?.removeEventListener('drop', popupDragEvent)
      }
    }, [dragAreaRef, handleDropFile, uploadingPdf, disabled])

    console.log('@@@FullscreenDrag:', {
      uploadingPdf,
      paringPdf2Html,
      showBlurArea,
    })

    return (
      <div
        className={classNames(style.popup, {
          [style.show]: showBlurArea,
        })}
        ref={(node) => (popupAreaRef.current = node)}
      >
        {uploadingPdf || paringPdf2Html ? (
          <div className={classNames(style.uploading, style.msgArea)}>
            <img
              className={classNames(style.icon)}
              src={commonUtils.getStaticFile('pdf/upload-file.png')}
            />
            <p className={classNames(style.uploadText, style.tip1)}>上传中...</p>
            <p className={classNames(style.tip2)}>初次上传需要一定时间进行解析，请您耐心等候～</p>
            <div className={classNames(style.container)}>
              <Progress
                percent={Math.floor(uploadPercent)}
                className={style.progressBarOverlay}
                style={{ transitionDuration: '0' }}
              />
              <div
                className={classNames(style.rocket)}
                style={{
                  transform: `translate(${
                    -12 + handleCalcProgressLeft(Math.floor(uploadPercent))
                  }px, 0)`,
                }}
              >
                <img
                  className={classNames(style.rocketIcon)}
                  src={commonUtils.getStaticFile('pdf/upload-rocket.png')}
                />
              </div>
            </div>

            <div className={classNames(style.cancelWrap)} onClick={() => handleCancelUpload()}>
              <img
                className={classNames(style.icon)}
                src={commonUtils.getStaticFile('pdf/upload-icon.png')}
              />
              <span>取消上传</span>
            </div>
          </div>
        ) : disabled ? null : (
          <div className={classNames(style.msgArea)}>
            <img
              className={classNames(style.icon)}
              src={commonUtils.getStaticFile('pdf/upload-file.png')}
            />
            <p className={classNames(style.tip1)}>请将文件拖拽至此</p>
            <p className={classNames(style.tip2)}>仅支持PDF格式、文件大小不超过100M</p>
          </div>
        )}
      </div>
    )
  })
)

export default FullscreenDrag
